import { useState } from 'react'
import { CalendarDays, Pencil, Settings2 } from 'lucide-react'
import { command, useProject } from '../app/data'
import { appStore } from '../app/state'
import { Badge, fleetStatus, Modal } from './ui'
import { ActionForm, dateTime, Empty, number, type Field, type Values } from './forms'
import { Attachments } from './Attachments'
import { EquipmentTypeIcon, ObjectRows, Tabs } from './controls'
import { eventLabels, workStatus } from '../domain/selectors'
import { WorkOrderDetail } from './workOrders'
import type { Equipment } from '../domain/models'

export const equipmentFields: Field[] = [
  { name: 'type', label: 'Тип техники' },
  {
    name: 'typeKey',
    label: 'Нормализованный вид',
    type: 'select',
    options: [
      { value: 'excavator', label: 'Экскаватор' },
      { value: 'bulldozer', label: 'Бульдозер' },
      { value: 'wheel_loader', label: 'Погрузчик' },
      { value: 'motor_grader', label: 'Автогрейдер' },
      { value: 'roller', label: 'Каток' },
      { value: 'dump_truck', label: 'Самосвал' },
      { value: 'crane', label: 'Кран' },
      { value: 'truck', label: 'Автотранспорт' },
      { value: 'other', label: 'Прочая техника' },
    ],
  },
  { name: 'sourceTypeName', label: 'Исходный вид из источника', required: false },
  { name: 'sourceTypeId', label: 'Код вида во внешней системе', required: false },
  {
    name: 'mappingStatus',
    label: 'Сопоставление вида',
    type: 'select',
    options: [
      { value: 'recognized', label: 'Распознано автоматически' },
      { value: 'review', label: 'Требует проверки' },
      { value: 'manual', label: 'Сопоставлено вручную' },
    ],
  },
  { name: 'model', label: 'Модель' },
  { name: 'plate', label: 'Госномер' },
  { name: 'garageNumber', label: 'Гаражный номер', required: false },
  { name: 'inventoryNumber', label: 'Инвентарный номер', required: false },
  { name: 'vin', label: 'VIN / заводской номер', required: false },
  {
    name: 'year',
    label: 'Год выпуска',
    type: 'number',
    min: 1900,
    max: new Date().getFullYear() + 1,
    required: false,
  },
  { name: 'specification', label: 'Характеристики', type: 'textarea', required: false, wide: true },
  {
    name: 'status',
    label: 'Операционный статус (не технический допуск)',
    type: 'select',
    options: Object.entries(fleetStatus).map(([value, s]) => ({ value, label: s.label })),
  },
  { name: 'ownershipType', label: 'Владение (собственная / аренда / лизинг)', required: false },
  { name: 'department', label: 'Подразделение', required: false },
  { name: 'location', label: 'Место базирования', required: false },
  { name: 'commissionedAt', label: 'Ввод в эксплуатацию', type: 'date', required: false },
  { name: 'engineType', label: 'Тип двигателя', required: false },
  { name: 'tankVolume', label: 'Объем бака, л', type: 'number', min: 0, required: false },
  { name: 'registrationDocument', label: 'Регистрационный документ', required: false },
  { name: 'documentIssuedAt', label: 'Дата выдачи', type: 'date', required: false },
  { name: 'insuranceUntil', label: 'Страхование до', type: 'date', required: false },
  { name: 'diagnosticCardUntil', label: 'Диагностическая карта до', type: 'date', required: false },
  { name: 'fuelCardNumber', label: 'Топливная карта', required: false },
  { name: 'fuelType', label: 'Основной вид топлива' },
  { name: 'fuelNorm', label: 'Норматив расхода', type: 'number', min: 0.01 },
  {
    name: 'fuelDeviationPercent',
    label: 'Сигнал при отклонении, %',
    type: 'number',
    min: 0,
    max: 100,
  },
  {
    name: 'season',
    label: 'Сезонность эксплуатации',
    type: 'select',
    options: [
      { value: 'year_round', label: 'Круглогодичная' },
      { value: 'winter', label: 'Зимняя' },
      { value: 'summer', label: 'Летняя' },
      { value: 'period', label: 'По заданному периоду' },
    ],
  },
  { name: 'seasonStart', label: 'Начало сезонной эксплуатации', type: 'date', required: false },
  { name: 'seasonEnd', label: 'Окончание сезонной эксплуатации', type: 'date', required: false },
  { name: 'assignedPerson', label: 'Ответственный', required: false },
  { name: 'phone', label: 'Телефон ответственного', required: false },
  { name: 'externalId', label: 'Внешний идентификатор', required: false },
  { name: 'sourceSystem', label: 'Источник карточки', required: false },
]
export function EquipmentForm({ equipment, onDone }: { equipment?: Equipment; onDone: () => void }) {
  const project = useProject()
  const typeOptions: { value: string; label: string }[] = project.typeCatalog?.length
    ? project.typeCatalog.map(({ key, name }) => ({ value: key, label: name }))
    : [...(equipmentFields.find((field) => field.name === 'typeKey')?.options ?? [])]
  if (equipment?.typeKey && !typeOptions.some((option) => option.value === equipment.typeKey)) {
    typeOptions.push({ value: equipment.typeKey, label: `${equipment.typeKey} · прежняя классификация` })
  }
  const defaultMeters: Values[] = equipment?.meters?.length
    ? equipment.meters.map((meter) => ({ ...meter }))
    : [
        {
          id: 'primary',
          name: 'Основной счётчик',
          unit: equipment?.meterUnit || 'мч',
          value: equipment?.meter || 0,
          readingAt: equipment?.readingAt || '',
          source: equipment?.sourceSystem || 'Ручной ввод',
          active: true,
          primary: true,
        },
      ]
  return (
    <ActionForm
      initial={
        equipment
          ? { ...equipment, year: equipment.year || '', meters: defaultMeters }
          : {
              status: 'base',
              season: 'year_round',
              meter: 0,
              due: 500,
              interval: 500,
              dueType: 'ТО-1',
              fuelPercent: 0,
              fuelType: 'Дизельное топливо',
              fuelNorm: 18,
              fuelDeviationPercent: 15,
              typeKey: 'other',
              mappingStatus: 'review',
              sourceSystem: 'Ручной ввод',
              meters: defaultMeters,
            }
      }
      fields={equipmentFields.map((field) => field.name === 'typeKey' ? { ...field, options: typeOptions } : field)}
      submit={(v, key) =>
        command(
          'equipment.save',
          equipment?.id ?? '',
          {
            ...v,
            year: Number(v.year) || 0,
            tankVolume: Number(v.tankVolume) || 0,
            fuelNorm: Number(v.fuelNorm) || 0,
            fuelDeviationPercent: Number(v.fuelDeviationPercent) || 0,
            meters: ((v.meters as Values[] | undefined) ?? defaultMeters).map((meter, index) => ({
              ...meter,
              id: index === 0 ? 'primary' : String(meter.id || `meter-${index + 1}`),
              value: Number(meter.value) || 0,
              active: index === 0 ? true : meter.active !== false,
              primary: index === 0,
            })),
          },
          key,
        )
      }
      onDone={onDone}
    >
      {(values, set) => (
        <ObjectRows
          title="Счётчики техники"
          rows={(values.meters as Values[] | undefined) ?? defaultMeters}
          onChange={(rows) => set('meters', rows)}
          empty={{
            id: '',
            name: 'Дополнительный счётчик',
            unit: 'мч',
            value: 0,
            readingAt: '',
            source: 'Ручной ввод',
            active: true,
            primary: false,
          }}
          fields={[
            { name: 'name', label: 'Название' },
            {
              name: 'unit',
              label: 'Единица',
              type: 'select',
              options: [
                { value: 'мч', label: 'Моточасы' },
                { value: 'км', label: 'Километры' },
                { value: 'ч', label: 'Часы установки' },
              ],
            },
            { name: 'source', label: 'Источник' },
            { name: 'active', label: 'Активен', type: 'checkbox', required: false },
          ]}
        />
      )}
    </ActionForm>
  )
}
export function EquipmentDetail({
  id,
  onClose,
  onBaseline,
}: {
  id: string
  onClose: () => void
  onBaseline: (e: Equipment) => void
}) {
  const d = useProject()
  const e = d.equipment.find((e) => e.id === id)
  const [tab, setTab] = useState('overview')
  const [edit, setEdit] = useState(false)
  const [settings, setSettings] = useState(false)
  const [eventType, setEventType] = useState('')
  const [work, setWork] = useState('')
  if (!e) return null
  const works = d.workOrders.filter((w) => w.equipmentId === id)
  const maintenance = d.maintenanceHistory.filter((m) => m.equipmentId === id)
  const history = [
    ...d.events.filter((ev) => ev.entityId === id || works.some((w) => w.id === ev.entityId)),
    ...d.shifts
      .filter((s) => s.equipmentId === id)
      .map((s) => ({
        id: s.id,
        createdAt: s.createdAt,
        action: 'shift.close',
        actor: number(s.workHours) + ' ч',
      })),
    ...maintenance.map((m) => ({
      id: m.id,
      createdAt: m.createdAt,
      action: 'maintenance.close',
      actor: m.type + ' · ' + number(m.meter) + ' ' + (e.meterUnit ?? 'мч'),
    })),
    ...d.movements
      .filter((m) => m.equipmentId === id)
      .map((m) => ({
        id: m.id,
        createdAt: m.createdAt,
        action: m.quantity < 0 ? 'stock.issue' : 'stock.receive',
        actor: m.name + ' · ' + number(m.quantity) + ' ' + m.unit,
      })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  return (
    <Modal title={e.plate + ' · ' + e.model} onClose={onClose}>
      {edit ? (
        <EquipmentForm equipment={e} onDone={() => setEdit(false)} />
      ) : (
        <>
          <div className="detail-actions">
            <EquipmentTypeIcon type={e.type} />
            <Badge tone={fleetStatus[e.status].tone}>{fleetStatus[e.status].label}</Badge>
            <button className="button secondary" onClick={() => setEdit(true)}>
              <Pencil size={16} />
              Редактировать
            </button>
            <button className="button secondary" onClick={() => setSettings(true)}>
              <Settings2 size={16} />
              Регламент ТО
            </button>
          </div>
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { value: 'overview', label: 'Карточка' },
              {
                value: 'documents',
                label: 'Документы',
                count: d.artifacts.filter((a) => a.equipmentId === id).length,
              },
              { value: 'calendar', label: 'Календарь ТО' },
              { value: 'history', label: 'История событий' },
            ]}
          />
          {tab === 'overview' && (
            <>
              <div className="metric-grid compact">
                <article className="metric-card">
                  <span>Наработка</span>
                  <strong>
                    {number(e.meter)} <small>{e.meterUnit ?? 'мч'}</small>
                  </strong>
                </article>
                <article className="metric-card">
                  <span>До {e.dueType}</span>
                  <strong>
                    {number(e.due - e.meter)} <small>{e.meterUnit ?? 'мч'}</small>
                  </strong>
                </article>
              </div>
              <dl className="detail-grid">
                {equipmentFields
                  .filter((f) => f.name !== 'status')
                  .map((f) => (
                    <div key={f.name}>
                      <dt>{f.label}</dt>
                      <dd>{String(e[f.name as keyof Equipment] || '—')}</dd>
                    </div>
                  ))}
                <div>
                  <dt>Оператор</dt>
                  <dd>{e.operator || 'Не закреплен'}</dd>
                </div>
              </dl>
              <button className="button secondary" onClick={() => onBaseline(e)}>
                Сверить начальные показания
              </button>
            </>
          )}
          {tab === 'documents' && <Attachments binding={{ equipmentId: id }} />}
          {tab === 'calendar' && (
            <>
              <div className="section-heading">
                <h3>
                  <CalendarDays size={18} />
                  План обслуживания
                </h3>
                <button className="button secondary" onClick={() => appStore.navigate('maintenance')}>
                  Открыть автоматическое ТО
                </button>
              </div>
              <p>
                Следующее {e.dueType}: {number(e.due)} {e.meterUnit ?? 'мч'} · уведомление за{' '}
                {number(e.alertBefore)} {e.meterUnit ?? 'мч'}
              </p>
              <p>ТО назначается по наработке; дату выполнения можно запланировать в заказ-наряде.</p>
              {works.length ? (
                works.map((w) => (
                  <button className="event-list-row" key={w.id} onClick={() => setWork(w.id)}>
                    <strong>{w.title}</strong>
                    <span>
                      {dateTime(w.startAt)} — {dateTime(w.endAt)}
                    </span>
                    <Badge>{workStatus[w.status]}</Badge>
                  </button>
                ))
              ) : (
                <Empty>Нет запланированных работ</Empty>
              )}
              <h3>Выполненное обслуживание</h3>
              {maintenance.map((m) => (
                <p key={m.id}>
                  {dateTime(m.createdAt)} · {m.type} · {number(m.meter)} {e.meterUnit ?? 'мч'} · {m.comment}
                </p>
              ))}
            </>
          )}
          {tab === 'history' && (
            <>
              <label className="filter-control">
                Тип события
                <select value={eventType} onChange={(ev) => setEventType(ev.target.value)}>
                  <option value="">Все события</option>
                  {[...new Set(history.map((ev) => ev.action))].map((action) => (
                    <option key={action} value={action}>
                      {eventLabels[action] ?? action}
                    </option>
                  ))}
                </select>
              </label>
              <div className="event-timeline">
                {history
                  .filter((ev) => !eventType || ev.action === eventType)
                  .map((ev) => (
                    <article key={ev.id}>
                      <span>{dateTime(ev.createdAt)}</span>
                      <strong>{eventLabels[ev.action] ?? ev.action}</strong>
                      <small>{ev.actor}</small>
                    </article>
                  ))}
              </div>
              {!history.length && <Empty>История пока пуста</Empty>}
            </>
          )}
        </>
      )}
      {settings && (
        <Modal title="Индивидуальный регламент ТО" onClose={() => setSettings(false)}>
          <ActionForm
            initial={{
              intervalTO1: e.intervalTO1,
              intervalTO2: e.intervalTO2,
              interval: e.interval,
              alertBefore: e.alertBefore,
              criticalBefore: e.criticalBefore,
            }}
            fields={[
              ...(e.meterUnit === 'км'
                ? [{ name: 'interval', label: 'Интервал сервиса, км', type: 'number' as const, min: 1 }]
                : [
                    { name: 'intervalTO1', label: 'Интервал ТО-1, мч', type: 'number' as const, min: 1 },
                    { name: 'intervalTO2', label: 'Интервал ТО-2, мч', type: 'number' as const, min: 1 },
                  ]),
              {
                name: 'alertBefore',
                label: 'Предупреждать за ' + (e.meterUnit ?? 'мч'),
                type: 'number',
                min: 0,
              },
              {
                name: 'criticalBefore',
                label: 'Критичный порог, ' + (e.meterUnit ?? 'мч'),
                type: 'number',
                min: 0,
              },
            ]}
            submit={(v, key) => command('equipment.service', id, v, key)}
            onDone={() => setSettings(false)}
          />
          <p>
            Новый интервал применяется к следующему циклу после завершения ТО. Ближайшая плановая отметка
            сохраняется.
          </p>
        </Modal>
      )}
      {work && <WorkOrderDetail id={work} onClose={() => setWork('')} />}
    </Modal>
  )
}
